import { X, AlertTriangle } from "lucide-react";

interface Props {
  title: string;
  message: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onClose: () => void;
  /** Optional — callers with an async onConfirm that can fail should manage
   * these themselves (set true/clear before calling, catch and set an error
   * message, leave the modal open) so the failure is visible instead of a
   * silent unhandled rejection that leaves the modal stuck with no feedback. */
  executing?: boolean;
  error?: string | null;
}

export default function ConfirmModal({ title, message, confirmLabel = "Delete", onConfirm, onClose, executing = false, error = null }: Props) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl bg-red-500/15 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-destructive" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-foreground font-inter">{title}</h2>
            <p className="text-sm text-muted-foreground font-inter mt-1">{message}</p>
          </div>
          <button onClick={onClose} disabled={executing} className="text-muted-foreground hover:text-foreground transition-colors shrink-0 disabled:opacity-40">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-muted-foreground font-inter mb-3">This action cannot be undone.</p>
        {error && (
          <p className="text-xs text-destructive font-inter mb-4 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            {error}
          </p>
        )}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={executing}
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-xl transition-colors font-inter disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={executing}
            className="px-4 py-2 text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 rounded-xl transition-colors font-inter disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {executing ? "Deleting…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
